import React from 'react';
import {
    Alert,
    Box,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { getFormatedStringFromDate } from '../../utils/dateUtils';
import { MaintenanceIntervention } from '../../types/cutterHistory';
import CutterTooltip from '../../common/CutterTooltip';

interface CutterDetailsHistoryServicesProps {
    commonDataGridProps: any;
    interventions: MaintenanceIntervention[];
    selectedRowId?: number | null;
}

const interventionColumns: Array<GridColDef<MaintenanceIntervention>> = [
    {
        field: 'interventionDate',
        headerName: `Date`,
        minWidth: 140, flex: 1,
        valueFormatter: (value) => getFormatedStringFromDate(value),
    },
    {
        field: 'serviceActName', headerName: 'Act', flex: 1, minWidth: 100,
        renderCell: ({ value, row }: GridRenderCellParams<MaintenanceIntervention, string>) =>
            row.serviceActType ? (
                <CutterTooltip title={row.serviceActType} severity="info" placement="top">
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>{value}</Box>
                </CutterTooltip>
            ) : <>{value}</>,
    },
    {
        field: 'refCounterValue',
        headerName: 'Counter value',
        minWidth: 110, flex: 1,
        type: 'number',
        renderCell: ({ value, row }: GridRenderCellParams<MaintenanceIntervention, number>) => {
            if (value == null) return null;
            const label = [row.refCounterName, row.refCounterUnit].filter(Boolean).join(' ');
            const formatted = `${value.toFixed(2)}${row.refCounterUnit ? ` ${row.refCounterUnit}` : ''}`;
            return label ? (
                <CutterTooltip title={label} severity="info" placement="top">
                    <Box sx={{ cursor: 'help' }}>{formatted}</Box>
                </CutterTooltip>
            ) : <>{formatted}</>;
        },
    },
    {
        field: 'serviceActDurationSec',
        headerName: 'Duration',
        minWidth: 70, flex: 1,
        type: 'number',
        valueFormatter: (value?: number) => {
            if (value == null) return '';
            const h = Math.floor(value / 3600);
            const m = Math.floor((value % 3600) / 60);
            const s = Math.round(value % 60);
            const parts = [];
            if (h > 0) parts.push(`${h}h`);
            if (m > 0) parts.push(`${m}m`);
            if (s > 0 || parts.length === 0) parts.push(`${s}s`);
            return parts.join(' ');
        },
    },
    { field: 'userName', headerName: 'User', minWidth: 100, flex: 1,
        renderCell: ({ value, row }: GridRenderCellParams<MaintenanceIntervention, string>) => {
            const display = value?.replace(/lectra/gi, '').trim() ?? '';
            const details = [`Level: ${row.userLevel}`, row.softwareVersion].filter(Boolean).join(' - Software: v');
            return details ? (
                <CutterTooltip title={details} severity="info" placement="top">
                    <Box sx={{ cursor: 'help' }}>{display}</Box>
                </CutterTooltip>
            ) : <>{display}</>;
        },
    },
    { field: 'comment', headerName: 'Comment', flex: 1, minWidth: 160,
        renderCell: ({ value }: GridRenderCellParams<MaintenanceIntervention, string>) =>
            value ? (
                <CutterTooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>} severity="info" copyText={value} placement="top">
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>{value}</Box>
                </CutterTooltip>
            ) : null,
    }
];


export const CutterDetailsHistoryServices: React.FC<CutterDetailsHistoryServicesProps> = ({
    interventions,
    selectedRowId,
    commonDataGridProps,
}) => {
    return (
            interventions.length === 0 ? (
                <Alert severity="info">No intervention history available for this cutter.</Alert>
            ) : (
                <DataGrid
                     {...commonDataGridProps}
                    rows={interventions}
                    columns={interventionColumns}
                    getRowId={(row) => row.id}
                    density="compact"
                    disableRowSelectionOnClick
                    {...(selectedRowId != null
                        ? { rowSelectionModel: { type: 'include', ids: new Set([selectedRowId]) }, onRowSelectionModelChange: () => {} }
                        : {})}
                />
            )
    );
};

export default CutterDetailsHistoryServices;
